import React, { useCallback, useMemo, useState } from 'react';
import { AvTooltip, ColumnReorder, ExportButton, NoDataFound, Select, SwitchToggle, TextInput } from '@fabric-ui/components';
import { Search } from '@fabric-ui/icons';
import { flex, refetchingStyle } from '@fabric-ui/theme';
import { Box, Button, debounce, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import AvBreadcrumbs from '../../components/AvBreadcrumbs';
import AvFilters from '../../components/AvFilters';
import AvMenu from '../../components/AvMenu';
import AvProjTable from '../../components/AvProjTable';
import SavedViewPopper from '../../components/SavedViewPopper';
import SavedViewSelect from '../../components/SavedViewSelect';
import { useAvContext } from '../../context/AvContextProvider';
import { useExportQuerySqlWorkflow } from '../../hooks/useExportQueryWorklflow';
import { PAGE_PATHS, Permission } from '../../types';
import { ScreenType } from '../../types/savedViews.types';
import { useLocation } from '../../utils/AvRouter';
import { camelCaseToTitleCase, serializeSortItem } from '../../utils/utils';
import useDataForEntity, { DeleteEntitiesDialog, useCleanQueryFilter, useSavedViews } from './hooks';

const EntitiesPageStyle = {
  backgroundColor: theme => theme.palette.colors.neutrals[100],
  ...flex.col,
  gap: 3,
  px: 4,
  flexGrow: 1,
};
export const defaultFilters = {
  tablePage: ['0'],
  activeEntityId: ['Finding'],
  orderBy: [],
  investigationMode: ['false'],
};
const investigationModeToBool = investigationMode => investigationMode === 'true';
const rowLimit = 100;

function Entities() {
  const [selectedViewChanged, setSelectedViewChanged] = useState(false);
  const {
    filters,
    setFilters,
    entitiesOptions,
    searchValue,
    setSearchValue,
    updateFilters,
    activeProjName,
    headCells,
    page,
    textColumnsShown,
    rows,
    isLoading,
    isRefetching,
    count,
    allSQL,
    initHeadCells,
    filterOptions,
    orderBy,
    aggProjs,
    investigationMode,
    activeProj,
  } = useDataForEntity({ rowLimit, setSelectedViewChanged });
  const viewScreenType = ScreenType.Entities;
  const {
    selectViewRef,
    saveAsViewButtonRef,
    selectedView,
    usersInfo,
    userViews,
    onChangeView,
    selectedViewIdArray,
    viewsRefetch,
    openEditViewPopper,
    onDeleteView,
    setOpenEditViewPopper,
    getDefaultViewByEntity,
    getDefaultFiltersForEntity,
    SaveViewButtons,
  } = useSavedViews({
    viewScreenType,
    filters,
    setFilters,
    defaultFilters,
    controlledSelectedViewChanged: selectedViewChanged,
    setControlledSelectedViewChanged: setSelectedViewChanged,
    activeProjName,
    defaultEnabled: false,
  });
  const {
    userPermissions: { hasAllowedPermission },
  } = useAvContext();
  const [deletePopupOpened, setDeletePopupOpened] = useState(false);
  const location = useLocation({ select: ({ state }) => ({ state }) });
  const breadCrumbs = useMemo(
    () => (location.state?.breadCrumbs ? [...location.state.breadCrumbs, window.location.pathname] : undefined),
    []
  );
  const debouncedUpdateFilters = useCallback(
    debounce(v => {
      updateFilters('searchArray', v ? [v, ...textColumnsShown] : []);
    }, 500),
    [updateFilters]
  );
  const clearAllFilters = () => {
    setFilters({
      ...defaultFilters,
      ...getDefaultFiltersForEntity(activeProjName),
      activeEntityId: [aggProjs[activeProjName].entityTypeId.name],
      investigationMode: [investigationMode],
    });
    setSearchValue('');
  };
  const cleanFilter = useCleanQueryFilter(filters);

  const { mutate: exportSqlWorkflow, isPending: isExporting } = useExportQuerySqlWorkflow();

  const onColumnPickerApply = items =>
    updateFilters(
      'headCells',
      items.filter(({ hidden }) => !hidden).map(({ id }) => id)
    );
  const onChangeEntity = newProjName => {
    const defaultView = getDefaultViewByEntity(newProjName);
    const newFilters = {
      ...defaultFilters,
      ...getDefaultFiltersForEntity(newProjName),
      ...defaultView.config,
      viewId: defaultView.id ? [defaultView.id] : [],
      activeEntityId: [aggProjs[newProjName].entityTypeId.name],
      investigationMode: [investigationMode],
    };
    setFilters(newFilters);
  };

  const onSort = (property, direction) => {
    const isAsc = direction === 'asc';
    const nextOrderBy = [...orderBy.filter(item => item.property !== property).slice(0, 0), ...(direction ? [{ property, isAsc }] : [])];
    updateFilters('orderBy', nextOrderBy.map(serializeSortItem));
  };

  const tableProps = () => ({
    headCells,
    rows,
    rowLimit,
    loading: isLoading || isRefetching,
    sx: { ...(isRefetching && refetchingStyle) },
    pagination: true,
    paginationProps: { onPageChange: page => updateFilters('tablePage', [page], true), totalRows: count, pageNum: page },
    resizable: true,
    onSort,
    storageKey: `EntitiesExplorer ${activeProjName}`,
    multiSelect: false,
    allowEdit: false,
    isSelectionControlled: false,
    enableSelectionColor: false,
    hasHeaderFilters: false,
  });
  const queryClient = useQueryClient();
  const savedViewsOptions = useMemo(
    () => userViews.filter(({ config }) => config.activeEntityId[0] === aggProjs[activeProjName].entityTypeId.name),
    [userViews, activeProjName]
  );

  const initialColumns = useMemo(() => initHeadCells(activeProjName).filter(f => !f.id?.includes('.sources.')), [activeProjName]);

  return (
    <Box sx={EntitiesPageStyle}>
      {breadCrumbs && <AvBreadcrumbs breadcrumbs={breadCrumbs} />}
      <Typography variant="h3" sx={{ mb: 0 }}>
        Entity Explorer
      </Typography>
      <Box sx={{ ...flex.row, gap: 2 }}>
        <Box sx={{ ...flex.row, gap: 1 }}>
          <Box sx={{ '> .MuiButtonBase-root:first-of-type': { ...flex.justifyBetween, width: 250 } }}>
            <Select
              style={{ width: 250 }}
              size="small"
              value={activeProjName}
              onChange={onChangeEntity}
              options={entitiesOptions}
              getValueFunc={({ projId }: any) => projId.name}
              getLabelFunc={({ projDisplayName }) => camelCaseToTitleCase(projDisplayName)}
              showSelection
              isRequired
            />
          </Box>
          <Box
            ref={selectViewRef}
            sx={{ ...flex.itemsCenter, gap: 2, '> .MuiButtonBase-root:first-of-type': { ...flex.justifyBetween, width: 250 } }}>
            <SavedViewSelect
              selectedViewId={selectedViewIdArray[0]}
              onChange={onChangeView}
              options={savedViewsOptions}
              userToDisplayName={usersInfo}
              viewsRefetch={viewsRefetch}
              onDeleteView={onDeleteView}
              openViewPopper={() => setOpenEditViewPopper(true)}
              defaultEnabled={false}
            />
            <SavedViewPopper
              view={selectedView || {}}
              anchorEl={selectViewRef.current}
              popperProps={{ open: openEditViewPopper, isEditMode: true }}
              handleClose={() => setOpenEditViewPopper(false)}
              refetchViews={viewsRefetch}
              screenType={viewScreenType}
            />
          </Box>
        </Box>
        <Divider flexItem orientation="vertical" variant="normal" />
        <TextInput
          size="small"
          helperText="Search..."
          icon={<Search />}
          value={searchValue}
          onChange={v => {
            setSearchValue(v);
            debouncedUpdateFilters(v);
          }}
          sx={{ width: '450px' }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ ...flex.itemsCenter, px: 1, gap: 1 }} ref={saveAsViewButtonRef}>
          {SaveViewButtons}
        </Box>
      </Box>
      <Box sx={{ ...flex.justifyBetweenStart, gap: 2, '> div': { minHeight: 40 } }}>
        <AvFilters
          key={activeProjName}
          setFilters={setFilters}
          updateFilters={updateFilters}
          filters={filters}
          onClearFilters={clearAllFilters}
          dims={filterOptions}
          activeProjName={activeProjName}
          displayRecommendedFields={false}
        />
        <Box sx={{ ...flex.row, ...flex.itemsCenter, gap: 2, flexShrink: 0 }}>
          <SwitchToggle
            label="Investigate"
            value={investigationModeToBool(investigationMode)}
            onChange={investigationMode => updateFilters('investigationMode', investigationMode ? ['true'] : ['false'])}
            labelPlacement="start"
          />
          <Divider flexItem orientation="vertical" variant="middle" sx={{ height: '24px' }} />
          <Box sx={{ ...flex.row }}>
            <ExportButton
              disabled={isRefetching}
              loading={isExporting}
              onClick={() => exportSqlWorkflow({ querySql: allSQL, projectionID: activeProj })}
              totalRowsCount={count}
            />
            <ColumnReorder
              items={headCells.filter(f => !f.id?.includes('.sources.'))}
              setItems={onColumnPickerApply}
              initialColumns={initialColumns}
            />
            {hasAllowedPermission({ path: PAGE_PATHS.MODEL_MANAGEMENT, permission: Permission.DELETE }) && (
              <AvMenu
                muiProps={{
                  getOptionDisabled: option => option.disabled,
                  renderOption: (props, { title, disabled }) => (
                    <AvTooltip title={disabled ? 'Apply filter to enable' : undefined}>
                      <span>
                        <li {...props}>
                          <div>{title}</div>
                        </li>
                      </span>
                    </AvTooltip>
                  ),
                }}
                options={[{ title: 'Delete by Current Filter', onClick: () => setDeletePopupOpened(true), disabled: !cleanFilter }]}
              />
            )}
          </Box>
        </Box>
      </Box>
      {count !== 0 ? (
        <AvProjTable {...tableProps()} activeProjName={activeProjName} />
      ) : (
        <NoDataFound
          headline={`No ${aggProjs[activeProjName].projDisplayName} Found`}
          callToAction={
            <>
              Try to <Button onClick={clearAllFilters}>Clear All filters</Button>
            </>
          }
        />
      )}
      <DeleteEntitiesDialog
        open={deletePopupOpened}
        onClose={() => setDeletePopupOpened(false)}
        totalCount={count || 0}
        projId={aggProjs[activeProjName].projId}
        filter={filters}
        onSuccess={() => queryClient.resetQueries({ queryKey: ['entityExplorer'] })}
      />
    </Box>
  );
}
export default Entities;
